import {Fetch} from "helpers/fetchWrapper";
import {useEffect, useState} from "react";
import {useCancellablePromise} from "helpers/promiseHandler";

const STATUS = {
    LOADING: "loading",
    SUCCESS: "success",
    ERROR: "error",
};

const orderService = {
    _url: `${process.env.REACT_APP_ORDER_DELIVERY_URL}`,

    GetOrders(searchQuery, refetch) {
        const [orders, setOrders] = useState({
            orders: [],
            totalItems: 0,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [status, setStatus] = useState(STATUS.LOADING);
        useEffect(() => {
            setStatus(STATUS.LOADING);
            cancellablePromise(this.getOrders(searchQuery))
                .then(res => {
                    setOrders(() => res);
                    setStatus(STATUS.SUCCESS);
                })
                .catch(() => setStatus(STATUS.ERROR));
        }, [JSON.stringify(searchQuery), refetch]);
        return {
            order_loading: status === STATUS.LOADING,
            order_error: status === STATUS.ERROR ? status : "",
            order_data: orders,
        };
    },

    async getOrders({
        page = 1,
        pageSize = 10,
        searchText = "",
        getAll = false,
        restaurantId = "",
        storeId = "",
        status = "",
        orderType = "",
        startDate = "",
        endDate = "",
    }) {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("itemsPerPage", pageSize);
        if (searchText) params.append("searchText", searchText);
        if (getAll) params.append("getAll", getAll);
        if (restaurantId) params.append("restaurant", restaurantId);
        if (storeId) params.append("store", storeId);
        if (status && status !== "All Status") params.append("status", status);
        if (orderType && orderType !== "All Type") params.append("orderType", orderType);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        let res = await Fetch.get(`${this._url}/order/get-all-orders?${params.toString()}`);
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something went wrong");
    },


    async updateOrderStatus(orderId, status, additionalData = {}) {
        let res = await Fetch.put(`${this._url}/order/handle-order-status/${orderId}`, {status, ...additionalData});

        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Something went wrong");
    },

    // Get restaurant order history with filters and pagination
    GetOrderHistory(searchQuery, refetch) {
        const [orderHistory, setOrderHistory] = useState({
            orders: [],
            totalItems: 0,
            totalPages: 0,
            currentPage: 1,
        });
        const {cancellablePromise} = useCancellablePromise();
        const [status, setStatus] = useState(STATUS.LOADING);

        useEffect(() => {
            setStatus(STATUS.LOADING);
            cancellablePromise(this.getOrderHistory(searchQuery))
                .then(res => {
                    setOrderHistory(
                        () =>
                            res?.ordersData || {
                                orders: [],
                                totalItems: 0,
                                totalPages: 0,
                                currentPage: 1,
                            }
                    );
                    setStatus(STATUS.SUCCESS);
                })
                .catch(err => {
                    console.error("Error fetching order history:", err);
                    setStatus(STATUS.ERROR);
                });
        }, [JSON.stringify(searchQuery), refetch]);

        return {
            history_loading: status === STATUS.LOADING,
            history_error: status === STATUS.ERROR ? status : "",
            history_data: orderHistory,
        };
    },

    async getOrderHistory({
        restaurant,
        store = "",
        page = 1,
        itemsPerPage = 10,
        searchText = "",
        startDate = "",
        endDate = "",
        status = "",
        orderType = "",
        paymentMethod = "",
    }) {
        const params = new URLSearchParams();
        params.append("restaurant", restaurant);
        params.append("page", page);
        params.append("itemsPerPage", itemsPerPage);

        if (store && store.trim()) params.append("store", store);
        if (searchText && searchText.trim()) params.append("searchText", searchText);
        // Send dates in yyyy-mm-dd format (ISO format)
        if (startDate && startDate.trim() && startDate.length === 10) params.append("startDate", startDate);
        if (endDate && endDate.trim() && endDate.length === 10) params.append("endDate", endDate);
        if (status && status !== "all") params.append("status", status);
        if (orderType && orderType !== "all") params.append("orderType", orderType);
        if (paymentMethod && paymentMethod !== "all") params.append("paymentMethod", paymentMethod);

        console.log("📡 Order History API params:", params.toString());

        let res = await Fetch.get(`${this._url}/order/restaurant-order-history?${params.toString()}`);

        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to fetch order history");
    },

    // Re-dispatch order after post-pickup driver cancellation (food returned & OK)
    async redispatchOrder(orderId) {
        let res = await Fetch.post(`${this._url}/order/${orderId}/redispatch`, {});
        if (res.status >= 200 && res.status < 300) {
            res = await res.json();
            return res;
        }
        const {message} = await res.json();
        throw new Error(message ?? "Failed to redispatch order");
    },
};
export default orderService;
